import {Translator} from '@logistics-frontend/core/i18n'
import React, {lazy, PropsWithChildren, Suspense} from 'react'

import {logEvent} from '../event'
import {getRumGlobal} from '../utils'

enum ErrorBoundaryTemplate {
  splashScreen = 'splashScreen',
  topBanner = 'topBanner',
}

type ErrorBoundaryProps = PropsWithChildren<{
  template?: ErrorBoundaryTemplate
  i18n: Translator
}>

type ErrorBoundaryState = {
  hasError: boolean
}

const ErrorAlert = lazy(
  () => import(/* webpackChunkName: 'error-boundary-alert' */ '@logistics-frontend/core/ui/alerts'),
)

const ErrorNotify = lazy(
  () => import(/* webpackChunkName: 'error-boundary-notify' */ '@logistics-frontend/blocks/error/ErrorNotify'),
)

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private fallbackComponent?: JSX.Element

  static defaultProps = {
    template: ErrorBoundaryTemplate.splashScreen,
  }

  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {hasError: false}
  }

  static getDerivedStateFromError() {
    return {hasError: true}
  }

  componentDidMount() {
    const t = this.props.i18n
    const reqId = getRumGlobal()?.getErrorSetting?.('reqid' as keyof Ya.Rum.LogErrorInitSettings['reqid'])
    const fallbackComponents: {[key in ErrorBoundaryTemplate]: JSX.Element} = {
      topBanner: <Suspense children={<ErrorAlert />} />,
      splashScreen: <Suspense children={<ErrorNotify {...{reqId, t}} />} />,
    }

    this.fallbackComponent = fallbackComponents[this.props.template!]
  }

  componentDidCatch(error: Ya.Rum.LoggableError, additional: React.ErrorInfo) {
    const {logError} = getRumGlobal() || {}
    const commonArgs: [Ya.Rum.LogErrorOptions, Ya.Rum.LoggableError] = [
      {additional, type: 'error-boundary'},
      error || new Error('MISSING_ERROR'),
    ]

    logError?.(...commonArgs)
    logEvent('error_boundary_shown')
  }

  render() {
    return this.state.hasError ? this.fallbackComponent : this.props.children
  }
}
